/* eslint-disable react/self-closing-comp */
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  MessageSquare,
  MessageSquareWarning,
  EllipsisVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useGetPostByIdQuery,
  useDeletePostDeleteMutation,
} from '@/redux/post/post.api.slice';
import PostCommentForm from '@/redux/commet/PostCommentForm';
import PostCommentList from '@/redux/commet/PostCommentList';
import LikeButton from '@/redux/like/LikeButton';
import NotFound from '../NotFound';
import ShareButton from '@/components/Post/ShareButton';
import DownloadPostAttachementsModal from '@/redux/post/DownloadPostAttachementsModal';
import { Card } from '@/components/ui/card';
import SectionHeading from '@/components/Post/SectionHeading';
import PostDetailBody from './PostDetailBody';
import { IPostVisibility } from '@/redux/post/post.d';
import Loader from '@/components/common/Loader';
import BookmarkButton from '@/redux/bookmark/BookmarkButton';
import UploadMakeModal from '@/redux/projectMake/UploadMakeModal';
import {
  ProjectMakes,
  useGetProjectMakesByPostQuery,
} from '@/redux/projectMake';
import RatingButton from '@/redux/projectMake/RatingButton';
import AdminCommentsPanel from '@/components/AdminComments/AdminCommentsPanel';
import { UserState } from '@/redux/auth/auth.d';
import { Button } from '@/components/ui/button';
import Notice from '@/components/ui/notice';

function ModalDetail() {
  const { id: postId } = useParams();
  const navigate = useNavigate();
  const { data: post, isLoading, refetch } = useGetPostByIdQuery(postId || '');
  const { data: makes, isLoading: isMakesLoading } =
    useGetProjectMakesByPostQuery({ postId: postId || '' });
  const { user } = useSelector((state: { auth: UserState }) => state.auth);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [deletePost] = useDeletePostDeleteMutation();
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isOwner = Boolean(user && post?.user._id === user._id);

  // Mirrors the two server-side rules (403 own project / 409 duplicate) so the
  // CTA isn't offered for a submission the API would reject. When it is blocked,
  // say why — an invitation that silently vanishes just reads as a missing feature.
  const hasOwnMake = Boolean(
    user && makes?.some((make) => make.user?._id === user._id)
  );

  let makeAction = null;
  if (!isOwner && !hasOwnMake) {
    makeAction = <UploadMakeModal postId={postId ?? ''} />;
  } else if (hasOwnMake) {
    makeAction = (
      <p className="rounded-md border border-border p-4 text-sm text-muted-foreground">
        You&apos;ve already shared a make for this project — one per maker.
      </p>
    );
  } else if (isOwner) {
    makeAction = (
      <p className="rounded-md border border-border p-4 text-sm text-muted-foreground">
        Makes come from other makers who cut your project — you can&apos;t post
        one on your own.
      </p>
    );
  }

  const handleEdit = () => {
    navigate(`/project/v2/edit/${postId}`);
  };

  const handleDelete = async () => {
    if (!postId) return;
    try {
      await deletePost(postId).unwrap();
      toast.success('Project deleted');
      navigate(-1);
    } catch (error) {
      toast.error('Failed to delete the project. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Scroll to a make card when navigated with a hash (e.g. #make-<id>)
  useEffect(() => {
    if (isMakesLoading || !makes?.length) return;
    const { hash } = window.location;
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [makes, isMakesLoading]);

  // Get admin comments from post object
  const adminComments = useMemo(() => {
    return post?.adminComments || [];
  }, [post]);

  // Rejection notes are the owner's business only, and only while unpublished.
  const canSeeAdminFeedback =
    isOwner &&
    (post?.visibility === IPostVisibility.REJECTED_WITH_COMMENTS ||
      post?.visibility === IPostVisibility.PRIVATE) &&
    adminComments.length > 0;

  if (isLoading) return <Loader title="Just a moment!" description="" />;

  if (!post || !postId || !post.attachments || !post.attachments.length) {
    return <NotFound />;
  }

  return (
    <div
      className="scroll-snap-type-y-mandatory pb-24"
      style={{ marginTop: 'var(--nav-height)' }}
    >
      {post?.visibility === 'private' && (
        <Notice className="mx-5 mt-4">
          This project will not be visible to others until it is approved by
          filefinity.
        </Notice>
      )}
      <PostDetailBody
        post={post}
        titleActions={
          isOwner && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={handleEdit}>
                  Edit Project
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteDialogOpen(true)}
                  className="focus:text-red-500 focus:bg-red-200"
                >
                  Delete Project
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
        notice={
          canSeeAdminFeedback && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAdminPanelOpen(true)}
              className="gap-2 border-yellow-600 text-yellow-700 hover:bg-yellow-50"
            >
              <MessageSquareWarning className="h-4 w-4" />
              Admin Feedback ({adminComments.length})
            </Button>
          )
        }
        interactions={
          <>
            <LikeButton
              liked={post?.hasLikedByUser}
              postId={postId}
              likes={post?.likes}
              size="md"
            />
            <RatingButton
              averageTotalRating={post?.averageTotalRating}
              makes={makes}
              isLoading={isMakesLoading}
              size="md"
            />
            <BookmarkButton
              bookmarked={post?.hasBookmarkedByUser}
              postId={postId}
              size="icon"
            />
            <ShareButton
              shareUrl={`${window.location.origin}/model/${postId}`}
              size="icon"
            />
          </>
        }
        download={
          <DownloadPostAttachementsModal
            attachments={post?.attachments}
            postTitle={post.title}
            licenceType={post?.licenceType}
            refetch={refetch}
            downloadCount={post.downloadCount ?? 0}
          />
        }
      />
      {/* ponytail: comments and makes share the row 50/50, each with its own
          header; items-start so neither column stretches the other */}
      <div className="border-t border-border scroll-snap-align-start mt-8 pt-8 pb-8 mx-auto px-4 lg:px-14 flex items-start gap-10 lg:flex-nowrap flex-wrap">
        {/* Makes lead — they're the proof the project works. Comments sit beside
            them. Each column is one white panel under a bare heading, so nothing
            renders straight onto the page canvas and neither column nests cards. */}
        <div className="w-full lg:w-1/2 flex flex-col gap-4">
          <ProjectMakes postId={postId} action={makeAction} />
        </div>
        <div className="w-full lg:w-1/2 flex flex-col gap-4">
          <SectionHeading
            icon={<MessageSquare className="w-5 h-5 text-primary" />}
            count={post?.comments}
          >
            Comments
          </SectionHeading>
          <Card className="p-4 md:p-5 flex flex-col">
            <PostCommentForm postId={postId} />
            {/* Rule + space so the list doesn't run straight into the composer. */}
            <div className="mt-5 pt-5 border-t border-border">
              <PostCommentList postId={postId} />
            </div>
          </Card>
        </div>
      </div>

      {canSeeAdminFeedback && (
        <AdminCommentsPanel
          comments={adminComments}
          isOpen={isAdminPanelOpen}
          onClose={() => setIsAdminPanelOpen(false)}
        />
      )}

      {isDeleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
            <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this project? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModalDetail;
